import React, { useState, useMemo, useEffect, useContext } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faReceipt,
  faFilter,
  faSpinner,
  faCheckCircle,
  faClose,
  faTimes,
  faUtensils,
  faBagShopping,
  faMotorcycle,
  faClock,
  faCheck,
  faHourglassHalf,
  faMoneyBillWave,
  faComment,
  faRefresh,
  faMinus,
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
  faPercent,
  faUndo,
  faPrint,
  faEye,
  faBan,
  faReorder,
  faTable,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import PayBillsModal from "./PayBillsModal";
import MessageModal from "./MessageModal";
import ReceiptPrinterEncoder from "@point-of-sale/receipt-printer-encoder";
import AddOrderModal from "./AddOrderModal";
import { RoleContext } from "./App";

// Helper to fetch CSRF token
const fetchCsrfToken = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/session/token`,
    {
      method: "GET",
      credentials: "include",
    },
  );
  if (!response.ok) {
    throw new Error("Failed to fetch CSRF token");
  }
  return await response.text();
};

// Simple fetcher for the orders API
const ordersFetcher = async (url) => {
  try {
    const response = await fetch(url, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    console.log("Order Data", data);
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

// Fetcher for tables API (JSON:API)
const tablesFetcher = async (url) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    },
  });
  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }
  return response.json();
};

// Items per page for pagination
const ITEMS_PER_PAGE = 20;

// ============ THERMAL RECEIPT GENERATOR ============
const generateThermalReceipt = (order, paymentMethod = "cash") => {
  const encoder = new ReceiptPrinterEncoder({
    language: "esc-pos",
    type: "epson",
  });

  const formatCurrency = (amount) => `GHC${parseFloat(amount || 0).toFixed(2)}`;
  const LINE_WIDTH = 42;

  const COL_WIDTHS = {
    item: 22,
    qty: 5,
    price: 10,
    label: 28,
    value: 14,
  };

  const padRight = (str, len) => {
    const s = String(str || "");
    return s.length > len ? s.substring(0, len) : s.padEnd(len);
  };

  const padLeft = (str, len) => {
    const s = String(str || "");
    return s.length > len ? s.substring(0, len) : s.padStart(len);
  };

  const formatItemLine = (name, qty, price) => {
    return `${padRight(name, COL_WIDTHS.item)}${padLeft(String(qty), COL_WIDTHS.qty)}${padLeft(price, COL_WIDTHS.price)}`;
  };

  const formatTotalLine = (label, amount) => {
    return `${padRight(label, COL_WIDTHS.label)}${padLeft(amount, COL_WIDTHS.value)}`;
  };

  const line = "=".repeat(LINE_WIDTH);
  const dashLine = "-".repeat(LINE_WIDTH);
  const orderIdStr = String(order?.id || "N/A").padStart(6, "0");

  const items = order?.items || [];
  const receiptGuestName = order?.guest_name || "Guest";
  const receiptTableNumber = order?.table_number || "";
  const receiptWaiter = order?.waiter_name || order?.user?.name || "";

  const orderTypeLabel =
    order?.order_type === "dine-in"
      ? "DINE-IN"
      : order?.order_type === "takeaway"
        ? "TAKEAWAY"
        : "DELIVERY";

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timeStr = now.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const paymentMethodLabel = paymentMethod.toUpperCase().replace("_", " ");

  // ✅ Calculate subtotal from active items (exclude voided)
  const subtotal = items.reduce((sum, item) => {
    if (item.status === "voided") return sum;
    const remainingQty = item.quantity - (item.refunded_quantity || 0);
    return sum + item.price * remainingQty;
  }, 0);

  const discount = order?.discount || 0;
  const hasDiscount = discount > 0;
  const discountedSubtotal = subtotal - discount;

  const isDineIn = order?.order_type === "dine-in";
  const serviceChargePercentage =
    order?.service_charge_percentage || (isDineIn ? 5 : 0);
  const serviceChargeAmount =
    discountedSubtotal * (serviceChargePercentage / 100);
  const hasServiceCharge = isDineIn && serviceChargeAmount > 0;

  const grandTotal = discountedSubtotal + serviceChargeAmount;
  const amountPaid = grandTotal;

  // Calculate tax breakdown based on discounted subtotal
  const taxRate = 0.21;
  const taxExclusiveAmount = discountedSubtotal / (1 + taxRate);
  const vatAmount = taxExclusiveAmount * 0.15;
  const nhilAmount = taxExclusiveAmount * 0.025;
  const getfundAmount = taxExclusiveAmount * 0.025;
  const tourismAmount = taxExclusiveAmount * 0.01;

  // Build 80mm wide receipt layout (42 characters)
  encoder
    .initialize()
    .align("center")
    .size(2)
    .bold(true)
    .line("THE HEADQUARTERS EATERY")
    .size(1)
    .bold(false)
    .line("21st Otele Avenue, Accra")
    .line("TIN: P0013030590")
    .line(line)
    .bold(true)
    .line(`ORDER #${orderIdStr}`)
    .bold(false)
    .line(`${dateStr}   ${timeStr}`)
    .line(`${orderTypeLabel}`)
    .line(line)
    .newline()
    .align("left")
    .line(`Customer: ${receiptGuestName}`)
    .line(`Table:    ${receiptTableNumber || "N/A"}`)
    .line(`Waiter:   ${receiptWaiter || "N/A"}`)
    .newline()
    .align("center")
    .line(dashLine)
    .align("left")
    .line(formatItemLine("Item Description", "Qty", "Price"))
    .line(dashLine);

  // ✅ Only print active (non-voided) items
  let hasRefundedItems = false;
  items.forEach((item) => {
    if (item.status === "voided") return;

    const qty = item.quantity || 0;
    const price = item.price || 0;
    const total = qty * price;
    const name = (item.name || "Unknown").substring(0, COL_WIDTHS.item);
    const refundedQty = item.refunded_quantity || 0;
    const remainingQty = qty - refundedQty;

    if (remainingQty > 0) {
      encoder.line(formatItemLine(name, qty.toString(), total.toFixed(2)));
      if (refundedQty > 0) {
        hasRefundedItems = true;
        encoder.line(`  (Refunded: -${refundedQty})`);
      }
    }
  });

  if (hasRefundedItems) {
    encoder.newline();
  }

  // ✅ Use calculated values
  encoder
    .newline()
    .align("center")
    .line(dashLine)
    .align("left")
    .line(formatTotalLine("Subtotal", formatCurrency(subtotal)));

  if (hasDiscount) {
    encoder.line(formatTotalLine("Discount", `-${formatCurrency(discount)}`));
  }

  if (hasServiceCharge) {
    encoder.line(
      formatTotalLine(
        `Service (${serviceChargePercentage}%)`,
        formatCurrency(serviceChargeAmount),
      ),
    );
  }

  // Show tax breakdown
  if (
    vatAmount > 0 ||
    nhilAmount > 0 ||
    getfundAmount > 0 ||
    tourismAmount > 0
  ) {
    encoder.line(dashLine);
    encoder.line(formatTotalLine("Tax Breakdown", ""));
    if (vatAmount > 0) {
      encoder.line(formatTotalLine("VAT 15%", formatCurrency(vatAmount)));
    }
    if (nhilAmount > 0) {
      encoder.line(formatTotalLine("NHIL 2.5%", formatCurrency(nhilAmount)));
    }
    if (getfundAmount > 0) {
      encoder.line(
        formatTotalLine("GETFund 2.5%", formatCurrency(getfundAmount)),
      );
    }
    if (tourismAmount > 0) {
      encoder.line(
        formatTotalLine("Tourism 1%", formatCurrency(tourismAmount)),
      );
    }
  }

  encoder
    .align("center")
    .line(line)
    .bold(true)
    .line(formatTotalLine("TOTAL", formatCurrency(grandTotal)))
    .bold(false)
    .line(line)
    .newline()
    .align("left")
    .line(`Payment: ${paymentMethodLabel}`)
    .line(`Amount:  ${formatCurrency(amountPaid)}`);

  encoder
    .newline()
    .align("center")
    .line(dashLine)
    .line("Thank you for dining with us!")
    .line("Have a great day!")
    .line(line)
    .newline()
    .line("Software by: Reactive System Developers")
    .line("0257335982")
    .newline()
    .newline()
    .newline()
    .newline()
    .newline()
    .cut("partial");

  return encoder.encode();
};

// ============ ORDER DETAILS MODAL COMPONENT ============
const OrderDetailsModal = ({
  order,
  onClose,
  onVoidItem,
  onDeleteItem,
  onVoidAll,
}) => {
  const [processing, setProcessing] = useState(false);
  const [voidQuantity, setVoidQuantity] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printerIP, setPrinterIP] = useState("192.168.1.233");
  const [printerPort, setPrinterPort] = useState(9100);

  useEffect(() => {
    const savedIP = localStorage.getItem("printer_ip");
    const savedPort = localStorage.getItem("printer_port");
    if (savedIP) setPrinterIP(savedIP);
    if (savedPort) setPrinterPort(parseInt(savedPort));
  }, []);

  if (!order) return null;

  const formatCurrency = (amount) => `GHC${parseFloat(amount || 0).toFixed(2)}`;

  const orderTypeInfo = {
    "dine-in": { icon: faUtensils, label: "Dine In", color: "#FF6B35" },
    takeaway: { icon: faBagShopping, label: "Takeaway", color: "#10B981" },
    delivery: { icon: faMotorcycle, label: "Delivery", color: "#3B82F6" },
  }[order.order_type] || {
    icon: faUtensils,
    label: "Dine In",
    color: "#FF6B35",
  };

  const subtotal =
    order.items?.reduce((sum, item) => {
      if (item.status === "voided") return sum;
      const remainingQty = item.quantity - (item.refunded_quantity || 0);
      return sum + item.price * remainingQty;
    }, 0) || 0;

  const discount = order.discount || 0;
  const hasDiscount = discount > 0;
  const discountedSubtotal = subtotal - discount;

  const serviceChargePercentage =
    order.service_charge_percentage || (order.order_type === "dine-in" ? 5 : 0);
  const serviceCharge = discountedSubtotal * (serviceChargePercentage / 100);
  const total = discountedSubtotal + serviceCharge;

  const voidedItems =
    order.items?.filter((item) => item.status === "voided") || [];
  const voidedTotal = voidedItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const isPaid = order.status === "paid";
  const cashierName = order.cashier_name || "N/A";
  const waiterName = order.waiter_name || order.user?.name || "N/A";

  // ✅ Check if all items are voided
  const areAllItemsVoided = () => {
    if (!order.items || order.items.length === 0) return true;
    return order.items.every((item) => item.status === "voided");
  };

  const allVoided = areAllItemsVoided();

  const handleVoidQuantity = async (item, index) => {
    const qtyToVoid = voidQuantity[index] || 0;
    if (qtyToVoid <= 0) {
      setErrorMessage("Please enter a quantity to void");
      setModalError(true);
      return;
    }

    const remainingQty = item.quantity - (item.refunded_quantity || 0);
    if (qtyToVoid > remainingQty) {
      setErrorMessage(`Cannot void more than ${remainingQty} items`);
      setModalError(true);
      return;
    }

    if (window.confirm(`Void ${qtyToVoid} of "${item.name}"?`)) {
      setProcessing(true);
      await onVoidItem(item, index, qtyToVoid);
      setVoidQuantity({ ...voidQuantity, [index]: 0 });
      setProcessing(false);
    }
  };

  const handleVoidAll = async () => {
    const nonVoidedItems =
      order.items?.filter((item) => item.status !== "voided") || [];
    if (nonVoidedItems.length === 0) {
      setErrorMessage("No items to void");
      setModalError(true);
      return;
    }

    if (
      window.confirm(`Are you sure you want to void ALL items in this order?`)
    ) {
      setProcessing(true);
      await onVoidAll(order);
      setProcessing(false);
    }
  };

  const handleQuantityChange = (index, value) => {
    const numValue = parseInt(value) || 0;
    setVoidQuantity({ ...voidQuantity, [index]: numValue });
  };

  return (
    <div className="modal" style={modalStyles.overlay}>
      <div className="overlay" onClick={onClose}></div>
      <div className="modal-content" style={modalStyles.content}>
        <div style={modalStyles.header}>
          <div>
            <h2 style={modalStyles.title}>Order #{order.id}</h2>
            <div style={modalStyles.metaInfo}>
              <span
                style={{
                  ...modalStyles.typeBadge,
                  backgroundColor: orderTypeInfo.bgColor || "#FEF3F2",
                  color: orderTypeInfo.color,
                }}
              >
                {orderTypeInfo.label}
              </span>
              {order.table_number && (
                <span style={modalStyles.metaBadge}>
                  Table {order.table_number}
                </span>
              )}
              {order.number_of_people && (
                <span style={modalStyles.metaBadge}>
                  {order.number_of_people} Guests
                </span>
              )}
              <span style={modalStyles.metaBadge}>
                {new Date(order.created).toLocaleString()}
              </span>
            </div>
          </div>
          <button style={modalStyles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.infoSection}>
            <div style={modalStyles.infoRow}>
              <span style={modalStyles.infoLabel}>Customer:</span>
              <span style={modalStyles.infoValue}>
                {order.guest_name || "Guest"}
              </span>
            </div>
            <div style={modalStyles.infoRow}>
              <span style={modalStyles.infoLabel}>Waiter/Server:</span>
              <span style={modalStyles.infoValue}>{waiterName}</span>
            </div>
            {isPaid && (
              <div style={modalStyles.infoRow}>
                <span style={modalStyles.infoLabel}>Cashier:</span>
                <span style={modalStyles.infoValue}>{cashierName}</span>
              </div>
            )}
            {order.special_requests && (
              <div style={modalStyles.infoRow}>
                <span style={modalStyles.infoLabel}>Special Requests:</span>
                <span style={modalStyles.infoValue}>
                  {order.special_requests}
                </span>
              </div>
            )}
            <div style={modalStyles.infoRow}>
              <span style={modalStyles.infoLabel}>Status:</span>
              <span
                style={{
                  ...modalStyles.statusBadge,
                  backgroundColor:
                    order.status === "paid" ? "#D1FAE5" : "#FEF3C7",
                  color: order.status === "paid" ? "#059669" : "#D97706",
                }}
              >
                {order.status?.toUpperCase()}
              </span>
            </div>
          </div>

          {/* ✅ Only show Void All button if NOT paid AND NOT all items are voided */}
          {order.status !== "paid" && !allVoided && (
            <div style={modalStyles.voidAllContainer}>
              <button
                style={modalStyles.voidAllButton}
                onClick={handleVoidAll}
                disabled={processing}
              >
                <FontAwesomeIcon icon={faBan} style={{ marginRight: "8px" }} />
                Void All Items
              </button>
            </div>
          )}

          {/* ✅ Show message if all items are voided */}
          {order.status !== "paid" && allVoided && (
            <div style={modalStyles.voidAllContainer}>
              <span style={modalStyles.allVoidedLabel}>
                <FontAwesomeIcon icon={faBan} style={{ marginRight: "8px" }} />
                All Items Already Voided
              </span>
            </div>
          )}

          <div style={modalStyles.tableContainer}>
            <div style={modalStyles.tableHeader}>
              <span style={{ flex: 3 }}>Item</span>
              <span style={{ width: "50px", textAlign: "center" }}>Qty</span>
              <span style={{ width: "80px", textAlign: "right" }}>Price</span>
              <span style={{ width: "80px", textAlign: "right" }}>Total</span>
              <span style={{ width: "240px", textAlign: "right" }}>
                Actions
              </span>
            </div>
            <div style={modalStyles.tableBody}>
              {order.items?.map((item, index) => {
                const isVoided = item.status === "voided";
                const refundedQty = item.refunded_quantity || 0;
                const remainingQty = item.quantity - refundedQty;
                const totalPrice = item.price * remainingQty;
                const isPaid = order.status === "paid";

                return (
                  <div
                    key={index}
                    style={{
                      ...modalStyles.tableRow,
                      opacity: isVoided ? 0.5 : 1,
                      backgroundColor: isVoided ? "#FEE2E2" : "transparent",
                    }}
                  >
                    <span
                      style={{
                        flex: 3,
                        textDecoration: isVoided ? "line-through" : "none",
                      }}
                    >
                      {item.name}
                      {isVoided && (
                        <span style={modalStyles.voidedBadge}> (Voided)</span>
                      )}
                      {item.notes && (
                        <span style={modalStyles.itemNotes}>
                          {" "}
                          ({item.notes})
                        </span>
                      )}
                    </span>
                    <span style={{ width: "50px", textAlign: "center" }}>
                      {remainingQty}
                    </span>
                    <span style={{ width: "80px", textAlign: "right" }}>
                      {formatCurrency(item.price)}
                    </span>
                    <span style={{ width: "80px", textAlign: "right" }}>
                      {formatCurrency(totalPrice)}
                    </span>
                    <span style={{ width: "240px", textAlign: "right" }}>
                      {!isVoided && !isPaid && (
                        <>
                          <input
                            type="number"
                            min="0"
                            max={remainingQty}
                            value={voidQuantity[index] || 0}
                            onChange={(e) =>
                              handleQuantityChange(index, e.target.value)
                            }
                            style={modalStyles.qtyInput}
                            disabled={processing}
                          />
                          <button
                            style={{
                              ...modalStyles.actionButton,
                              ...modalStyles.voidButton,
                            }}
                            onClick={() => handleVoidQuantity(item, index)}
                            disabled={
                              processing ||
                              !voidQuantity[index] ||
                              voidQuantity[index] <= 0
                            }
                            title="Void Quantity"
                          >
                            <FontAwesomeIcon icon={faBan} /> Void
                          </button>
                        </>
                      )}
                      {isVoided && (
                        <span style={modalStyles.voidedLabel}>Voided</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={modalStyles.totalsSection}>
            <div style={modalStyles.totalRow}>
              <span style={modalStyles.totalLabel}>Subtotal:</span>
              <span style={modalStyles.totalValue}>
                {formatCurrency(subtotal)}
              </span>
            </div>
            {hasDiscount && (
              <div style={{ ...modalStyles.totalRow, color: "#DC2626" }}>
                <span style={modalStyles.totalLabel}>Discount:</span>
                <span style={modalStyles.totalValue}>
                  -{formatCurrency(discount)}
                </span>
              </div>
            )}
            {serviceCharge > 0 && (
              <div style={modalStyles.totalRow}>
                <span style={modalStyles.totalLabel}>
                  Service Charge: ({serviceChargePercentage}%)
                </span>
                <span style={modalStyles.totalValue}>
                  {formatCurrency(serviceCharge)}
                </span>
              </div>
            )}
            {voidedTotal > 0 && (
              <div style={{ ...modalStyles.totalRow, color: "#DC2626" }}>
                <span style={modalStyles.totalLabel}>Voided Amount</span>
                <span style={modalStyles.totalValue}>
                  -{formatCurrency(voidedTotal)}
                </span>
              </div>
            )}
            <div style={modalStyles.totalDivider} />
            <div
              style={{ ...modalStyles.totalRow, ...modalStyles.grandTotalRow }}
            >
              <span style={modalStyles.grandTotalLabel}>Total</span>
              <span style={modalStyles.grandTotalValue}>
                {formatCurrency(total)}
              </span>
            </div>
          </div>
        </div>

        <div style={modalStyles.footer}>
          <button style={modalStyles.closeModalButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ MODAL STYLES ============
const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  content: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    width: "90%",
    maxWidth: "900px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    overflow: "hidden",
  },
  header: {
    padding: "24px 28px",
    borderBottom: "1px solid #E2E8F0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
    color: "#1F2937",
  },
  metaInfo: {
    display: "flex",
    gap: "15px",
    marginTop: "8px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  typeBadge: {
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "15px",
    fontWeight: "600",
  },
  metaBadge: {
    padding: "4px 12px",
    backgroundColor: "#F3F4F6",
    borderRadius: "20px",
    fontSize: "15px",
    color: "#4B5563",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "26px",
    cursor: "pointer",
    color: "#9CA3AF",
    padding: "8px",
    borderRadius: "8px",
    transition: "background 0.2s",
  },
  body: {
    padding: "24px 28px",
    overflowY: "auto",
    flex: 1,
  },
  infoSection: {
    backgroundColor: "#F8FAFC",
    padding: "16px 20px",
    borderRadius: "12px",
    marginBottom: "20px",
  },
  infoRow: {
    display: "flex",
    gap: "12px",
    marginBottom: "6px",
    fontSize: "14px",
  },
  infoLabel: {
    fontWeight: "600",
    color: "#6B7280",
    minWidth: "140px",
  },
  infoValue: {
    color: "#1F2937",
  },
  statusBadge: {
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "600",
  },
  voidAllContainer: {
    marginBottom: "16px",
    display: "flex",
    justifyContent: "flex-end",
  },
  voidAllButton: {
    padding: "10px 20px",
    backgroundColor: "#DC2626",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "background 0.2s",
    minHeight: "44px",
  },
  allVoidedLabel: {
    padding: "10px 20px",
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    display: "inline-flex",
    alignItems: "center",
    minHeight: "44px",
  },
  qtyInput: {
    width: "50px",
    padding: "4px 6px",
    border: "1px solid #E2E8F0",
    borderRadius: "6px",
    fontSize: "15px",
    textAlign: "center",
    marginRight: "4px",
    minHeight: "32px",
  },
  tableContainer: {
    border: "1px solid #E2E8F0",
    borderRadius: "12px",
    overflow: "hidden",
  },
  tableHeader: {
    display: "flex",
    padding: "12px 16px",
    backgroundColor: "#F8FAFC",
    fontWeight: "600",
    fontSize: "15px",
    color: "#6B7280",
    borderBottom: "1px solid #E2E8F0",
  },
  tableBody: {
    maxHeight: "300px",
    overflowY: "auto",
  },
  tableRow: {
    display: "flex",
    padding: "10px 16px",
    borderBottom: "1px solid #F1F1F1",
    fontSize: "15px",
    alignItems: "center",
    transition: "background 0.2s",
  },
  voidedBadge: {
    color: "#DC2626",
    fontSize: "15px",
    fontWeight: "600",
    marginLeft: "6px",
  },
  itemNotes: {
    color: "#FF6B35",
    fontSize: "15px",
    fontStyle: "italic",
    marginLeft: "4px",
  },
  actionButton: {
    padding: "16px 16px",
    border: "none",
    borderRadius: "6px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    marginLeft: "6px",
    transition: "all 0.2s",
  },
  voidButton: {
    backgroundColor: "#FEF3C7",
    color: "#D97706",
  },
  deleteButton: {
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
  },
  voidedLabel: {
    color: "#DC2626",
    fontSize: "15px",
    fontWeight: "600",
  },
  totalsSection: {
    marginTop: "20px",
    padding: "16px 20px",
    backgroundColor: "#F8FAFC",
    borderRadius: "12px",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
    fontSize: "15px",
  },
  totalLabel: {
    color: "#6B7280",
  },
  totalValue: {
    fontWeight: "500",
  },
  totalDivider: {
    borderTop: "1px solid #E2E8F0",
    margin: "8px 0",
  },
  grandTotalRow: {
    paddingTop: "8px",
  },
  grandTotalLabel: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1F2937",
  },
  grandTotalValue: {
    fontSize: "20px",
    fontWeight: "800",
    color: "#FF6B35",
  },
  footer: {
    padding: "16px 28px",
    borderTop: "1px solid #E2E8F0",
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  },
  printButton: {
    padding: "10px 20px",
    backgroundColor: "#3B82F6",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "background 0.2s",
  },
  closeModalButton: {
    padding: "10px 20px",
    backgroundColor: "#F3F4F6",
    color: "#4B5563",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background 0.2s",
  },
};

export default function OrderRegister() {
  const { stateNavigator } = useNavigationEvent();

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedWaiter, setSelectedWaiter] = useState("all");
  const [selectedCashier, setSelectedCashier] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showAddOrderModal, setShowAddOrderModal] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printerIP, setPrinterIP] = useState("192.168.1.233");
  const [printerPort, setPrinterPort] = useState(9100);

  // ✅ Table selection state
  const [selectedTable, setSelectedTable] = useState("all");

  // ✅ Use roles from context
  const { roles } = useContext(RoleContext);

  // ✅ Check if user is admin
  const isAdmin = roles?.some(
    (role) => role === "administrator" || role === "admin",
  );

  useEffect(() => {
    const savedIP = localStorage.getItem("printer_ip");
    const savedPort = localStorage.getItem("printer_port");
    if (savedIP) setPrinterIP(savedIP);
    if (savedPort) setPrinterPort(parseInt(savedPort));
  }, []);

  const formatDateForAPI = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getApiUrl = () => {
    const startDateParam = formatDateForAPI(startDate);
    const endDateParam = formatDateForAPI(endDate);

    if (startDateParam === endDateParam) {
      return `${process.env.REACT_APP_BASE_URL}/api/orders?date=${startDateParam}`;
    }

    return `${process.env.REACT_APP_BASE_URL}/api/orders?from_date=${startDateParam}&to_date=${endDateParam}`;
  };

  const {
    data: ordersResponse,
    isLoading: ordersLoading,
    error: ordersError,
    mutate: mutateOrders,
  } = useSWR(getApiUrl(), ordersFetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshInterval: 10000,
    revalidateIfStale: true,
    revalidateOnMount: true,
  });

  // ✅ Fetch tables from Table Manager API
  const {
    data: tablesData,
    error: tablesError,
    isLoading: tablesLoading,
  } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/jsonapi/taxonomy_term/restaurant_tables?sort=drupal_internal__tid`,
    tablesFetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 30000,
    },
  );

  // ✅ Process tables from JSON:API response
  const tables = useMemo(() => {
    if (!tablesData || !tablesData.data) return [];

    return tablesData.data.map((item) => {
      const attributes = item.attributes;
      const tableNumberMatch = attributes.name.match(/(\d+)/);
      const tableNumber = tableNumberMatch ? parseInt(tableNumberMatch[0]) : 0;

      return {
        id: item.id,
        table_number: tableNumber,
        table_display: attributes.name,
        status: attributes.field_table_status || "available",
      };
    });
  }, [tablesData]);

  const allOrders = ordersResponse?.data || [];

  // ✅ Get table numbers from the Table Manager API
  const tableNumbers = useMemo(() => {
    return tables.map((table) => table.table_number).sort((a, b) => a - b);
  }, [tables]);

  const cashierOptions = useMemo(() => {
    const cashiers = new Set();
    allOrders.forEach((order) => {
      const cashierName = order.cashier_name || "";
      if (cashierName) {
        cashiers.add(cashierName);
      }
    });
    return Array.from(cashiers).sort();
  }, [allOrders]);

  const waiterOptions = useMemo(() => {
    const waiters = new Set();
    allOrders.forEach((order) => {
      const waiterName = order.waiter_name || order.user?.name || "";
      if (waiterName) {
        waiters.add(waiterName);
      }
    });
    return Array.from(waiters).sort();
  }, [allOrders]);

  const calculateFoodRefundAmount = (order) => {
    if (!order.items || !order.items.length) return 0;
    let foodRefundedAmount = 0;
    order.items.forEach((item) => {
      const refundedQty = item.refunded_quantity || 0;
      if (item.status !== "voided" && refundedQty > 0) {
        foodRefundedAmount += item.price * refundedQty;
      }
    });
    return foodRefundedAmount;
  };

  const calculateCurrentSubtotal = (order) => {
    if (!order.items || !order.items.length) return 0;
    return order.items.reduce((sum, item) => {
      if (item.status === "voided") return sum;
      const remainingQty = item.quantity - (item.refunded_quantity || 0);
      return sum + item.price * remainingQty;
    }, 0);
  };

  const getRefundStatusInfo = (refundStatus) => {
    switch (refundStatus) {
      case "partial":
        return {
          text: "Partially Refunded",
          color: "#D97706",
          bgColor: "#FEF3C7",
          icon: faUndo,
        };
      case "full":
        return {
          text: "Fully Refunded",
          color: "#DC2626",
          bgColor: "#FEE2E2",
          icon: faUndo,
        };
      default:
        return null;
    }
  };

  const handleVoidItem = async (item, index, quantityToVoid = null) => {
    if (!selectedOrder) return;
    const qty =
      quantityToVoid !== null
        ? quantityToVoid
        : item.quantity - (item.refunded_quantity || 0);
    try {
      const csrfToken = await fetchCsrfToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/order/${selectedOrder.id}/void-item`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify({
            item_index: index,
            quantity: qty,
            reason: `Voided by staff (${qty} item${qty > 1 ? "s" : ""})`,
          }),
        },
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to void item");
      setSelectedOrder({
        ...selectedOrder,
        items: result.order.items,
        total: result.order.total,
        menu_subtotal: result.order.menu_subtotal,
      });
      setSuccessMessage(`✅ ${qty} of "${item.name}" has been voided`);
      setModalSuccessMsg(true);
      setTimeout(() => setModalSuccessMsg(false), 3000);
      mutateOrders();
    } catch (err) {
      setErrorMessage("Failed to void item: " + err.message);
      setModalError(true);
    }
  };

  const handleDeleteItem = async (item, index) => {
    if (!selectedOrder) return;
    try {
      const csrfToken = await fetchCsrfToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/order/${selectedOrder.id}/delete-item`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify({ item_index: index }),
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "Failed to delete item");
      setSelectedOrder({
        ...selectedOrder,
        items: result.order.items,
        total: result.order.total,
        menu_subtotal: result.order.menu_subtotal,
      });
      setSuccessMessage(`✅ Item "${item.name}" has been deleted`);
      setModalSuccessMsg(true);
      setTimeout(() => setModalSuccessMsg(false), 3000);
      mutateOrders();
    } catch (err) {
      setErrorMessage("Failed to delete item: " + err.message);
      setModalError(true);
    }
  };

  const handleVoidAll = async (order) => {
    if (!order) return;
    try {
      const csrfToken = await fetchCsrfToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/order/${order.id}/void-all`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify({ reason: "All items voided" }),
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.error || "Failed to void all items");
      setSelectedOrder({
        ...order,
        items: result.order.items,
        total: result.order.total,
        menu_subtotal: result.order.menu_subtotal,
      });
      setSuccessMessage(`✅ All items in Order #${order.id} have been voided`);
      setModalSuccessMsg(true);
      setTimeout(() => setModalSuccessMsg(false), 3000);
      mutateOrders();
    } catch (err) {
      setErrorMessage("Failed to void all items: " + err.message);
      setModalError(true);
    }
  };

  const handleReprintReceipt = async (order) => {
    if (printing) return;
    setPrinting(true);
    setErrorMessage("");

    try {
      localStorage.setItem("printer_ip", printerIP);
      localStorage.setItem("printer_port", printerPort.toString());

      const data = generateThermalReceipt(order);
      const dataArray = Array.from(data);

      const response = await fetch("http://localhost:3001/print", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ip: printerIP,
          port: printerPort,
          data: dataArray,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to print receipt");
      }

      setSuccessMessage("✅ Receipt reprinted successfully!");
      setModalSuccessMsg(true);
      setTimeout(() => {
        setModalSuccessMsg(false);
      }, 3000);
    } catch (error) {
      console.error("Reprint failed:", error);
      setErrorMessage(`Reprint failed: ${error.message}`);
      setModalError(true);
    } finally {
      setPrinting(false);
    }
  };

  // ✅ Filter orders with table selection
  const filteredOrders = useMemo(() => {
    if (!allOrders || allOrders.length === 0) return [];
    let filtered = [...allOrders];

    // ✅ Filter by table (dine-in only)
    if (selectedTable !== "all") {
      filtered = filtered.filter(
        (order) =>
          order.order_type === "dine-in" &&
          order.table_number === parseInt(selectedTable),
      );
    }

    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (order) => order.order_type === selectedCategory,
      );
    }

    if (selectedStatus !== "all") {
      filtered = filtered.filter((order) => {
        if (selectedStatus === "paid") return order.status === "paid";
        if (selectedStatus === "partially_refunded")
          return order.refund_status === "partial";
        if (selectedStatus === "refunded")
          return order.refund_status === "full";
        return order.status === selectedStatus;
      });
    }

    if (selectedWaiter !== "all") {
      filtered = filtered.filter((order) => {
        const waiterName = order.waiter_name || order.user?.name || "";
        return waiterName === selectedWaiter;
      });
    }

    if (selectedCashier !== "all") {
      filtered = filtered.filter((order) => {
        const cashierName = order.cashier_name || "";
        return cashierName === selectedCashier;
      });
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((order) => {
        const waiterName = order.waiter_name || order.user?.name || "";
        const cashierName = order.cashier_name || "";
        return (
          (order.guest_name &&
            order.guest_name.toLowerCase().includes(searchLower)) ||
          order.id.toString().includes(searchTerm) ||
          waiterName.toLowerCase().includes(searchLower) ||
          cashierName.toLowerCase().includes(searchLower)
        );
      });
    }
    return filtered;
  }, [
    allOrders,
    selectedTable,
    selectedCategory,
    selectedStatus,
    selectedWaiter,
    selectedCashier,
    searchTerm,
  ]);

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredOrders.slice(start, end);
  }, [filteredOrders, currentPage]);

  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedTable,
    selectedCategory,
    selectedStatus,
    selectedWaiter,
    selectedCashier,
    searchTerm,
    startDate,
    endDate,
  ]);

  const statistics = useMemo(() => {
    if (!filteredOrders.length) {
      return {
        totalSales: 0,
        totalRevenue: 0,
        totalServiceCharge: 0,
        totalRefundedAmount: 0,
        totalVoidedAmount: 0,
        dineInOrders: 0,
        takeawayOrders: 0,
        deliveryOrders: 0,
        totalGuests: 0,
        paidOrders: 0,
        partiallyRefundedOrders: 0,
        fullyRefundedOrders: 0,
        pendingPayment: 0,
        totalOrders: 0,
        totalDiscount: 0,
        activeTables: 0,
      };
    }

    const paidOrdersList = filteredOrders.filter(
      (order) => order.status === "paid",
    );

    const partiallyRefundedList = filteredOrders.filter(
      (order) => order.refund_status === "partial",
    );
    const fullyRefundedList = filteredOrders.filter(
      (order) => order.refund_status === "full",
    );

    const unpaidOrders = filteredOrders.filter(
      (order) => order.status !== "paid",
    );

    let totalSales = 0;
    let totalServiceCharge = 0;
    let totalRevenue = 0;
    let totalRefundedAmount = 0;
    let totalVoidedAmount = 0;
    let dineInOrders = 0;
    let takeawayOrders = 0;
    let deliveryOrders = 0;
    let totalGuests = 0;
    let totalDiscount = 0;

    // Count active tables (dine-in orders that are not paid)
    const activeTables = new Set();
    filteredOrders.forEach((order) => {
      if (
        order.order_type === "dine-in" &&
        order.status !== "paid" &&
        order.table_number
      ) {
        activeTables.add(order.table_number);
      }
    });

    filteredOrders.forEach((order) => {
      switch (order.order_type) {
        case "dine-in":
          dineInOrders++;
          break;
        case "takeaway":
          takeawayOrders++;
          break;
        case "delivery":
          deliveryOrders++;
          break;
        default:
          break;
      }
    });

    filteredOrders.forEach((order) => {
      if (order.order_type === "dine-in" && order.number_of_people) {
        totalGuests += order.number_of_people;
      }
    });

    paidOrdersList.forEach((order) => {
      const subtotal = calculateCurrentSubtotal(order);
      const discount = order.discount || 0;
      const discountedSubtotal = subtotal - discount;
      const scPercentage =
        order.service_charge_percentage ||
        (order.order_type === "dine-in" ? 5 : 0);
      const scAmount = discountedSubtotal * (scPercentage / 100);

      totalSales += discountedSubtotal;
      totalServiceCharge += scAmount;
      totalRevenue += discountedSubtotal + scAmount;
      totalDiscount += discount;
    });

    filteredOrders.forEach((order) => {
      if (order.items) {
        order.items.forEach((item) => {
          if (item.status === "voided") {
            totalVoidedAmount += item.price * item.quantity;
          }
        });
      }
    });

    partiallyRefundedList.forEach((order) => {
      const refundedFoodAmount =
        order.items?.reduce((sum, item) => {
          if (item.status === "voided") return sum;
          const refundedQty = item.refunded_quantity || 0;
          return sum + item.price * refundedQty;
        }, 0) || 0;

      const scPercentage =
        order.service_charge_percentage ||
        (order.order_type === "dine-in" ? 5 : 0);
      const refundedServiceCharge = refundedFoodAmount * (scPercentage / 100);
      totalRefundedAmount += refundedFoodAmount + refundedServiceCharge;
    });

    fullyRefundedList.forEach((order) => {
      const originalTotal = order.original_total || order.total || 0;
      totalRefundedAmount += originalTotal;
    });

    const pendingPayment = unpaidOrders.reduce((sum, order) => {
      const subtotal = calculateCurrentSubtotal(order);
      const discount = order.discount || 0;
      const discountedSubtotal = subtotal - discount;
      const scPercentage =
        order.service_charge_percentage ||
        (order.order_type === "dine-in" ? 5 : 0);
      const serviceCharge = discountedSubtotal * (scPercentage / 100);
      return sum + discountedSubtotal + serviceCharge;
    }, 0);

    return {
      totalSales,
      totalRevenue,
      totalServiceCharge,
      totalRefundedAmount,
      totalVoidedAmount,
      dineInOrders,
      takeawayOrders,
      deliveryOrders,
      totalGuests,
      paidOrders: paidOrdersList.length,
      partiallyRefundedOrders: partiallyRefundedList.length,
      fullyRefundedOrders: fullyRefundedList.length,
      pendingPayment,
      totalOrders: filteredOrders.length,
      totalDiscount,
      activeTables: activeTables.size,
    };
  }, [filteredOrders]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handlePayBills = (order) => {
    setSelectedOrder(order);
    setShowPayModal(true);
  };

  const handlePaymentSuccess = async () => {
    await mutateOrders();
    setTimeout(() => mutateOrders(), 1000);
  };

  const handleRefresh = async () => {
    setCurrentPage(1);
    await mutateOrders();
    const bustedUrl = `${getApiUrl()}&_=${Date.now()}`;
    await fetch(bustedUrl, { credentials: "include" });
    await mutateOrders();
  };

  const handleStartDateChange = (e) => {
    const newDate = new Date(e.target.value);
    newDate.setHours(0, 0, 0, 0);
    setStartDate(newDate);
    setCurrentPage(1);
  };

  const handleEndDateChange = (e) => {
    const newDate = new Date(e.target.value);
    newDate.setHours(23, 59, 59, 999);
    setEndDate(newDate);
    setCurrentPage(1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleOrderCreated = (order) => {
    mutateOrders();
  };

  const getOrderTypeInfo = (type) => {
    switch (type) {
      case "dine-in":
        return {
          icon: faUtensils,
          label: "Dine In",
          color: "#FF6B35",
          bgColor: "#FEF3F2",
        };
      case "takeaway":
        return {
          icon: faBagShopping,
          label: "Takeaway",
          color: "#10B981",
          bgColor: "#ECFDF5",
        };
      case "delivery":
        return {
          icon: faMotorcycle,
          label: "Delivery",
          color: "#3B82F6",
          bgColor: "#EFF6FF",
        };
      default:
        return {
          icon: faUtensils,
          label: "Dine In",
          color: "#FF6B35",
          bgColor: "#FEF3F2",
        };
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "received":
        return {
          icon: faClock,
          text: "Received",
          subtext: "Order Received",
          color: "#E65100",
          bgColor: "#FFF3E0",
        };
      case "preparing":
        return {
          icon: faHourglassHalf,
          text: "Preparing",
          subtext: "In the Kitchen",
          color: "#1565C0",
          bgColor: "#E3F2FD",
        };
      case "ready":
        return {
          icon: faCheck,
          text: "Ready",
          subtext: "Ready to serve",
          color: "#2E7D32",
          bgColor: "#E8F5E9",
        };
      case "paid":
        return {
          icon: faMoneyBillWave,
          text: "Paid",
          subtext: "Payment Completed",
          color: "#388E3C",
          bgColor: "#E8F5E9",
        };
      default:
        return {
          icon: faClock,
          text: status || "Pending",
          subtext: "Processing",
          color: "#616161",
          bgColor: "#F5F5F5",
        };
    }
  };

  // ✅ Check if all items in an order are voided
  const areAllItemsVoided = (order) => {
    if (!order.items || order.items.length === 0) return true;
    return order.items.every((item) => item.status === "voided");
  };

  const OrderCard = ({ order }) => {
    const orderType = getOrderTypeInfo(order.order_type);
    const statusInfo = getStatusInfo(order.status);
    const refundStatusInfo = getRefundStatusInfo(order.refund_status);
    const displayItems = order.items?.slice(0, 3) || [];
    const hasMoreItems = order.items?.length > 3;
    const displayNumber =
      order.order_type === "dine-in"
        ? `T${order.table_number || 1}`
        : order.order_type === "takeaway"
          ? "TA"
          : "DL";

    const subtotal = calculateCurrentSubtotal(order);
    const discount = order.discount || 0;
    const hasDiscount = discount > 0;
    const discountedSubtotal = subtotal - discount;
    const serviceChargePercentage =
      order.service_charge_percentage ||
      (order.order_type === "dine-in" ? 5 : 0);
    const showServiceCharge =
      order.order_type === "dine-in" && serviceChargePercentage > 0;
    const serviceCharge = showServiceCharge
      ? discountedSubtotal * (serviceChargePercentage / 100)
      : 0;
    const total = discountedSubtotal + serviceCharge;

    const refundedFoodAmount = calculateFoodRefundAmount(order);
    const hasRefund = refundStatusInfo !== null;
    const isPaid = order.status === "paid";
    const waiterName = order.waiter_name || order.user?.name || "N/A";
    const cashierName = order.cashier_name || "N/A";

    // ✅ Check if all items are voided
    const allVoided = areAllItemsVoided(order);

    return (
      <div style={styles.orderCard}>
        <div style={styles.orderCardHeader}>
          <div style={styles.orderHeaderLeft}>
            <div
              style={{
                ...styles.tableNumber,
                backgroundColor: orderType.bgColor,
                color: orderType.color,
              }}
            >
              {displayNumber}
            </div>
            <div>
              <span style={styles.orderId}>Order #{order.id}</span>
            </div>
            {order.order_type === "dine-in" && (
              <FontAwesomeIcon icon={faMinus} style={{ color: "#000000" }} />
            )}
            {order.order_type === "dine-in" && (
              <div style={styles.additionalInfo}>
                {order.number_of_people && (
                  <span style={styles.infoBadge}>
                    {order.number_of_people === 1 ? "Guest" : "Guests"} (
                    {order.number_of_people})
                  </span>
                )}
              </div>
            )}
          </div>
          <div style={styles.statusContainer}>
            <div
              style={{
                ...styles.statusBadge,
                backgroundColor: statusInfo.bgColor,
              }}
            >
              <span style={{ ...styles.statusText, color: statusInfo.color }}>
                {statusInfo.text}
              </span>
            </div>
            <div style={styles.statusSubtextContainer}>
              <span style={styles.timeText}>
                {new Date(order.created).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        </div>

        <div style={styles.staffContainer}>
          <div style={styles.waiterRow}>
            <span style={styles.waiterText}>Served By: {waiterName}</span>
          </div>
          {isPaid && (
            <div style={styles.cashierRow}>
              <span style={styles.cashierText}>Cashier: {cashierName}</span>
            </div>
          )}
        </div>

        {order.special_requests && (
          <div style={styles.specialRequests}>
            <FontAwesomeIcon
              icon={faComment}
              style={{ fontSize: "12px", color: "#FF6B35" }}
            />
            <span style={styles.specialRequestsText}>
              {order.special_requests}
            </span>
          </div>
        )}

        <div style={styles.itemsContainer}>
          <div style={styles.itemsTable}>
            <div style={styles.itemsTableHeader}>
              <div style={styles.headerItem}>Items</div>
              <div style={styles.headerQty}>Qty</div>
              <div style={styles.headerRefunded}>Refunded</div>
              <div style={styles.headerPrice}>Price</div>
            </div>
            <div style={styles.itemsTableBody}>
              {displayItems.map((item, idx) => {
                const refundedQty = item.refunded_quantity || 0;
                const remainingQty = item.quantity - refundedQty;
                const isRefundedItem = refundedQty > 0;
                const isVoided = item.status === "voided";
                return (
                  <div
                    key={idx}
                    style={{
                      ...styles.itemRow,
                      opacity: isVoided ? 0.5 : 1,
                      textDecoration: isVoided ? "line-through" : "none",
                    }}
                  >
                    <div style={styles.itemName}>
                      {item.name}
                      {item.notes && (
                        <span style={styles.itemNotes}> ({item.notes})</span>
                      )}
                      {isRefundedItem && (
                        <span style={styles.refundedBadge}>
                          {" "}
                          (Partially Refunded)
                        </span>
                      )}
                      {isVoided && (
                        <span style={styles.voidedBadge}> (Voided)</span>
                      )}
                    </div>
                    <div style={styles.itemQty}>
                      {remainingQty > 0 ? remainingQty : 0}
                    </div>
                    <div style={styles.itemRefunded}>
                      {refundedQty > 0 && (
                        <span style={{ color: "#DC2626" }}>-{refundedQty}</span>
                      )}
                    </div>
                    <div style={styles.itemPrice}>
                      GHC{(item.price * remainingQty).toFixed(2)}
                    </div>
                  </div>
                );
              })}
              {hasMoreItems && (
                <div style={styles.moreItemsRow}>
                  <div style={styles.moreItems}>
                    +{order.items.length - 3} more items
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={styles.orderCardFooter}>
          <div style={styles.totalContainer}>
            {hasRefund && refundedFoodAmount > 0 && (
              <div style={styles.footerItem}>
                <span style={styles.refundedLabel}>Refunded</span>
                <span style={styles.refundedValue}>
                  -GHC{refundedFoodAmount.toFixed(2)}
                </span>
              </div>
            )}
            <div style={styles.footerItem}>
              <span style={styles.footerLabel}>Subtotal :</span>
              <span style={styles.footerValue}>GHC{subtotal.toFixed(2)}</span>
            </div>
            {hasDiscount && (
              <div style={styles.footerItem}>
                <span style={styles.discountLabel}>Discount :</span>
                <span style={styles.discountValue}>
                  -GHC{discount.toFixed(2)}
                </span>
              </div>
            )}
            {showServiceCharge && (
              <div style={styles.footerItem}>
                <span style={styles.serviceChargeFooterLabel}>
                  + {serviceChargePercentage}% Service :
                </span>
                <span style={styles.serviceChargeFooterValue}>
                  GHC{serviceCharge.toFixed(2)}
                </span>
              </div>
            )}
            <div style={styles.footerDivider} />
            <div style={styles.footerItemTotal}>
              <span style={styles.footerTotalLabel}>Total :</span>
              <span style={styles.footerTotalValue}>GHC{total.toFixed(2)}</span>
            </div>
          </div>
          <div style={styles.actionButtons}>
            <button
              style={styles.detailsButton}
              onClick={() => handleViewOrder(order)}
            >
              <FontAwesomeIcon icon={faEye} style={{ marginRight: "6px" }} />{" "}
              Details
            </button>
            {isPaid && (
              <button
                style={styles.reprintButton}
                onClick={() => handleReprintReceipt(order)}
                disabled={printing}
                title="Reprint Receipt"
              >
                {printing ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faPrint}
                      style={{ marginRight: "6px" }}
                    />
                    Reprint
                  </>
                )}
              </button>
            )}
            {!isPaid && !allVoided && (
              <button
                style={styles.payButton}
                onClick={() => handlePayBills(order)}
              >
                Pay Bills
              </button>
            )}
            {!isPaid && allVoided && (
              <span style={styles.allVoidedLabel}>All Items Voided</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const categoryOptions = [
    { value: "all", label: "All Orders" },
    { value: "dine-in", label: "Dine In" },
    { value: "takeaway", label: "Takeaway" },
    { value: "delivery", label: "Delivery" },
  ];

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "received", label: "Received" },
    { value: "preparing", label: "Preparing" },
    { value: "ready", label: "Ready" },
    { value: "paid", label: "Paid" },
    { value: "partially_refunded", label: "Partially Refunded" },
    { value: "refunded", label: "Fully Refunded" },
  ];

  // Responsive styles to inject
  const responsiveStyles = `
    @media (max-width: 768px) {
      .content-wrapper {
        padding: 0 !important;
      }
      
      .dashboard-container {
        padding: 0 !important;
      }

      .table-buttons-grid {
        grid-template-columns: repeat(auto-fill, minmax(70px, 1fr)) !important;
        gap: 6px !important;
      }

      .table-button {
        min-height: 40px !important;
        min-width: 50px !important;
        font-size: 12px !important;
        padding: 6px 8px !important;
      }
    }

    @media (max-width: 480px) {
      .table-buttons-grid {
        grid-template-columns: repeat(auto-fill, minmax(60px, 1fr)) !important;
        gap: 4px !important;
      }

      .table-button {
        min-height: 36px !important;
        min-width: 40px !important;
        font-size: 11px !important;
        padding: 4px 6px !important;
      }

      .filters-bar select,
      .filters-bar .search-wrapper,
      .filters-bar .date-wrapper {
        flex: 1 1 100% !important;
        min-width: 100% !important;
        max-width: 100% !important;
      }
      
      .action-buttons {
        flex-direction: column !important;
        width: 100% !important;
      }
      
      .action-buttons button,
      .action-buttons span {
        width: 100% !important;
      }
    }

    @media (max-width: 380px) {
      .table-buttons-grid {
        grid-template-columns: repeat(auto-fill, minmax(50px, 1fr)) !important;
        gap: 3px !important;
      }

      .table-button {
        min-height: 32px !important;
        min-width: 35px !important;
        font-size: 10px !important;
        padding: 3px 4px !important;
        border-radius: 4px !important;
      }
    }
  `;

  if ((ordersLoading || tablesLoading) && !ordersResponse) {
    return (
      <div className="dashboard-container">
        <style>{responsiveStyles}</style>
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />

          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: "48px", color: "#FF6B35" }}
              />
              <p>Loading orders...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (ordersError && !ordersResponse) {
    return (
      <div className="dashboard-container">
        <style>{responsiveStyles}</style>
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.errorContainer}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ fontSize: "48px", color: "#d32f2f" }}
              />
              <h3>Error Loading Orders</h3>
              <p>
                Unable to connect to the orders API. Please check your
                connection.
              </p>
              <button style={styles.retryButton} onClick={handleRefresh}>
                <FontAwesomeIcon
                  icon={faRefresh}
                  style={{ marginRight: "8px" }}
                />{" "}
                Retry
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <style>{responsiveStyles}</style>
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          <TopActionMenus
            AddTitle="Order Register"
            buttonLabel="Add Order"
            onClickIcon={faPlus}
            onClick={() => setShowAddOrderModal(true)}
          />

          {/* STATISTICS CARDS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E8F5E9",
                }}
              >
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  GHC{statistics.totalSales.toFixed(2)}
                </h3>
                <p style={styles.statLabel}>Total Sales</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FFF3E0",
                }}
              >
                <FontAwesomeIcon
                  icon={faPercent}
                  style={{ color: "#E65100", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  GHC{statistics.totalServiceCharge.toFixed(2)}
                </h3>
                <p style={styles.statLabel}>Service Charge</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E3F2FD",
                }}
              >
                <FontAwesomeIcon
                  icon={faMoneyBillWave}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  GHC{statistics.totalRevenue.toFixed(2)}
                </h3>
                <p style={styles.statLabel}>Total Revenue</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E3F2FD",
                }}
              >
                <FontAwesomeIcon
                  icon={faReceipt}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalOrders}</h3>
                <p style={styles.statLabel}>Total Orders</p>
              </div>
            </div>
          </div>

          {/* ✅ TABLE BUTTONS - HORIZONTAL GRID THAT FITS FULL WIDTH */}
          <div style={styles.tableButtonsWrapper}>
            <div className="table-buttons-grid" style={styles.tableButtonsGrid}>
              <button
                className="table-button"
                style={{
                  ...styles.tableButton,
                  ...(selectedTable === "all" ? styles.tableButtonActive : {}),
                }}
                onClick={() => setSelectedTable("all")}
              >
                <span style={styles.tableButtonLabel}>All</span>
              </button>
              {tableNumbers.map((num) => (
                <button
                  key={num}
                  className="table-button"
                  style={{
                    ...styles.tableButton,
                    ...(selectedTable === num.toString()
                      ? styles.tableButtonActive
                      : {}),
                  }}
                  onClick={() => setSelectedTable(num.toString())}
                >
                  <span style={styles.tableButtonLabel}>T{num}</span>
                </button>
              ))}
              {tableNumbers.length === 0 && (
                <span style={styles.noTablesMessage}>
                  No tables found. Please add tables in Table Manager.
                </span>
              )}
            </div>
          </div>

          {/* FILTERS BAR */}
          <div className="filters-bar" style={styles.filtersBar}>
            <div style={styles.dateWrapper}>
              <FontAwesomeIcon
                icon={faCalendarAlt}
                style={{ color: "#FF6B35" }}
              />
              <label style={styles.dateLabel}>From:</label>
              <input
                type="date"
                style={styles.dateInput}
                value={startDate.toISOString().split("T")[0]}
                onChange={handleStartDateChange}
              />
            </div>
            {isAdmin && (
              <div style={styles.dateWrapper}>
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  style={{ color: "#FF6B35" }}
                />
                <label style={styles.dateLabel}>To:</label>
                <input
                  type="date"
                  style={styles.dateInput}
                  value={endDate.toISOString().split("T")[0]}
                  onChange={handleEndDateChange}
                />
              </div>
            )}
            <div style={styles.searchWrapper}>
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select
              style={styles.categorySelect}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categoryOptions.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            <select
              style={styles.statusSelect}
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              style={styles.waiterSelect}
              value={selectedWaiter}
              onChange={(e) => setSelectedWaiter(e.target.value)}
            >
              <option value="all">All Waiters</option>
              {waiterOptions.map((waiter) => (
                <option key={waiter} value={waiter}>
                  {waiter}
                </option>
              ))}
            </select>

            <select
              style={styles.cashierSelect}
              value={selectedCashier}
              onChange={(e) => setSelectedCashier(e.target.value)}
            >
              <option value="all">All Cashiers</option>
              {cashierOptions.map((cashier) => (
                <option key={cashier} value={cashier}>
                  {cashier}
                </option>
              ))}
            </select>
          </div>

          {/* ORDERS GRID */}
          {filteredOrders.length === 0 ? (
            <div style={styles.emptyState}>
              <FontAwesomeIcon
                icon={faReceipt}
                style={{ fontSize: "64px", color: "#CBD5E1" }}
              />
              <h3 style={styles.emptyTitle}>No Orders Found</h3>
              <p style={styles.emptySubtitle}>
                {selectedTable !== "all"
                  ? `No orders found for Table ${selectedTable}`
                  : searchTerm ||
                      selectedCategory !== "all" ||
                      selectedStatus !== "all" ||
                      selectedWaiter !== "all" ||
                      selectedCashier !== "all"
                    ? "No orders match your current filters"
                    : `No orders found for the selected date range`}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.ordersGrid}>
                {paginatedOrders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
              {totalPages > 1 && (
                <div style={styles.paginationContainer}>
                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: currentPage === 1 ? 0.5 : 1,
                    }}
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} /> Previous
                  </button>
                  <span style={styles.paginationInfo}>
                    Page {currentPage} of {totalPages} ({filteredOrders.length}{" "}
                    orders)
                  </span>
                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: currentPage === totalPages ? 0.5 : 1,
                    }}
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <DashboardContentFooter />

      {showOrderDetails && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrder(null);
          }}
          onVoidItem={handleVoidItem}
          onDeleteItem={handleDeleteItem}
          onVoidAll={handleVoidAll}
        />
      )}

      {showPayModal && selectedOrder && (
        <PayBillsModal
          order={selectedOrder}
          onClose={() => {
            setShowPayModal(false);
            setSelectedOrder(null);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}

      <AddOrderModal
        isOpen={showAddOrderModal}
        onClose={() => setShowAddOrderModal(false)}
        onOrderCreated={handleOrderCreated}
      />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage || "Operation completed successfully!"}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faFilter}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

// ============ STYLES ============
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    padding: "24px 16px 4rem 16px",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    color: "#d32f2f",
  },
  retryButton: {
    marginTop: "20px",
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "12px",
    marginBottom: "12px",
  },
  statCard: {
    background: "white",
    padding: "16px",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    transition: "transform 0.2s, box-shadow 0.2s",
    minWidth: "0",
  },
  statIconContainer: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statValue: {
    margin: 0,
    fontSize: "clamp(16px, 2vw, 24px)",
    fontWeight: "700",
    color: "#1F2937",
    wordBreak: "break-word",
  },
  statLabel: {
    margin: "4px 0 0 0",
    color: "#6B7280",
    fontSize: "clamp(11px, 1.2vw, 15px)",
    fontWeight: "500",
    wordBreak: "break-word",
  },
  tableButtonsWrapper: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "12px 12px",
    marginBottom: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #E5E7EB",
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  tableButtonsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
    gap: "16px",
    width: "100%",
  },
  tableButton: {
    padding: "10px 8px",
    backgroundColor: "#F3F4F6",
    border: "2px solid transparent",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "clamp(13px, 1.2vw, 15px)",
    fontWeight: "600",
    color: "#4B5563",
    transition: "all 0.2s ease",
    minHeight: "64px",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    userSelect: "none",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tableButtonActive: {
    backgroundColor: "#FF6B35",
    color: "white",
    borderColor: "#FF6B35",
    boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
    transform: "scale(1.02)",
  },
  tableButtonLabel: {
    fontWeight: "600",
    fontSize: "clamp(12px, 1.2vw, 15px)",
  },
  noTablesMessage: {
    fontSize: "clamp(12px, 1.2vw, 14px)",
    color: "#9CA3AF",
    fontStyle: "italic",
    padding: "10px 0",
    gridColumn: "1 / -1",
    textAlign: "center",
  },
  filtersBar: {
    display: "flex",
    gap: "10px",
    marginBottom: "24px",
    flexWrap: "wrap",
    alignItems: "center",
    width: "100%",
  },
  dateWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 12px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
    flex: "1 1 auto",
    minWidth: "140px",
    maxWidth: "100%",
  },
  dateLabel: {
    fontSize: "clamp(12px, 1.2vw, 14px)",
    fontWeight: "600",
    color: "#4B5563",
    whiteSpace: "nowrap",
  },
  dateInput: {
    border: "none",
    fontSize: "clamp(14px, 1.2vw, 20px)",
    fontWeight: "600",
    color: "#1F2937",
    outline: "none",
    backgroundColor: "transparent",
    padding: "4px",
    width: "100%",
    minWidth: "100px",
  },
  searchWrapper: {
    flex: "2 1 180px",
    minWidth: "120px",
    width: "100%",
  },
  searchInput: {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    fontSize: "clamp(14px, 1.2vw, 16px)",
    fontWeight: "500",
    color: "#1F2937",
    outline: "none",
    backgroundColor: "white",
    boxSizing: "border-box",
    minHeight: "44px",
  },
  categorySelect: {
    padding: "12px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    backgroundColor: "white",
    fontSize: "clamp(14px, 1.2vw, 16px)",
    fontWeight: "600",
    color: "#1F2937",
    cursor: "pointer",
    outline: "none",
    minWidth: "120px",
    flex: "1 1 auto",
    appearance: "auto",
    minHeight: "44px",
  },
  statusSelect: {
    padding: "12px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    backgroundColor: "white",
    fontSize: "clamp(14px, 1.2vw, 16px)",
    fontWeight: "600",
    color: "#1F2937",
    cursor: "pointer",
    outline: "none",
    minWidth: "120px",
    flex: "1 1 auto",
    appearance: "auto",
    minHeight: "44px",
  },
  waiterSelect: {
    padding: "12px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    backgroundColor: "white",
    fontSize: "clamp(14px, 1.2vw, 16px)",
    fontWeight: "600",
    color: "#1F2937",
    cursor: "pointer",
    outline: "none",
    minWidth: "120px",
    flex: "1 1 auto",
    appearance: "auto",
    minHeight: "44px",
  },
  cashierSelect: {
    padding: "12px 14px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    backgroundColor: "white",
    fontSize: "clamp(14px, 1.2vw, 16px)",
    fontWeight: "600",
    color: "#1F2937",
    cursor: "pointer",
    outline: "none",
    minWidth: "120px",
    flex: "1 1 auto",
    appearance: "auto",
    minHeight: "44px",
  },
  ordersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 550px), 1fr))",
    gap: "16px",
    width: "100%",
  },
  orderCard: {
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    overflow: "hidden",
    border: "1px solid #F0F0F0",
    transition: "transform 0.2s, box-shadow 0.2s",
    width: "100%",
    boxSizing: "border-box",
  },
  orderCardHeader: {
    padding: "16px 16px 8px",
    borderBottom: "1px solid #F0F0F0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "8px",
  },
  orderHeaderLeft: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  tableNumber: {
    width: "40px",
    height: "40px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "700",
    fontSize: "clamp(14px, 1.2vw, 18px)",
    flexShrink: 0,
  },
  orderId: {
    fontSize: "clamp(14px, 1.2vw, 16px)",
    color: "#000000",
    fontWeight: "600",
    letterSpacing: "1px",
    wordBreak: "break-word",
  },
  statusContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "4px",
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "clamp(12px, 1.2vw, 16px)",
    fontWeight: "600",
  },
  statusText: {
    fontSize: "clamp(12px, 1.2vw, 14px)",
    fontWeight: "600",
  },
  statusSubtextContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: "#9CA3AF",
  },
  timeText: {
    fontSize: "clamp(12px, 1.2vw, 14px)",
    fontWeight: "600",
    letterSpacing: "0.02em",
  },
  additionalInfo: {
    display: "flex",
    borderBottom: "1px solid #F0F0F0",
  },
  infoBadge: {
    fontSize: "clamp(13px, 1.2vw, 16px)",
    color: "#000000",
    alignItems: "center",
    fontWeight: "600",
    display: "flex",
    gap: "4px",
  },
  staffContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "6px 16px",
    backgroundColor: "#F8FAFC",
    borderBottom: "1px solid #F0F0F0",
  },
  waiterRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  waiterText: {
    fontSize: "clamp(13px, 1.2vw, 15px)",
    color: "#4B5563",
    fontWeight: "600",
    wordBreak: "break-word",
  },
  cashierRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  cashierText: {
    fontSize: "clamp(13px, 1.2vw, 15px)",
    color: "#3B82F6",
    fontWeight: "600",
    wordBreak: "break-word",
  },
  specialRequests: {
    padding: "6px 16px",
    backgroundColor: "#FEF3F2",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    borderBottom: "1px solid #F0F0F0",
  },
  specialRequestsText: {
    fontSize: "clamp(11px, 1.2vw, 12px)",
    color: "#FF6B35",
    fontStyle: "italic",
    wordBreak: "break-word",
  },
  itemsContainer: {
    padding: "12px 16px",
  },
  itemsTable: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  itemsTableHeader: {
    display: "flex",
    justifyContent: "space-between",
    paddingBottom: "6px",
    marginBottom: "6px",
    borderBottom: "1px solid #E5E7EB",
    fontWeight: "600",
    fontSize: "clamp(11px, 1.2vw, 13px)",
    color: "#6B7280",
  },
  headerItem: {
    flex: 2,
    textAlign: "left",
  },
  headerQty: {
    width: "40px",
    textAlign: "center",
  },
  headerRefunded: {
    width: "60px",
    textAlign: "center",
  },
  headerPrice: {
    width: "70px",
    textAlign: "right",
  },
  itemsTableBody: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  itemRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    fontSize: "clamp(12px, 1.2vw, 14px)",
  },
  itemName: {
    flex: 2,
    color: "#4B5563",
    wordBreak: "break-word",
    paddingRight: "8px",
  },
  itemNotes: {
    fontSize: "clamp(10px, 1.2vw, 11px)",
    color: "#FF6B35",
    fontStyle: "italic",
  },
  refundedBadge: {
    fontSize: "clamp(9px, 1.2vw, 10px)",
    color: "#DC2626",
    fontStyle: "italic",
    marginLeft: "4px",
  },
  voidedBadge: {
    fontSize: "clamp(9px, 1.2vw, 10px)",
    color: "#DC2626",
    fontWeight: "600",
    marginLeft: "4px",
  },
  itemQty: {
    width: "40px",
    textAlign: "center",
    color: "#6B7280",
  },
  itemRefunded: {
    width: "60px",
    textAlign: "center",
    fontSize: "clamp(10px, 1.2vw, 12px)",
  },
  itemPrice: {
    width: "70px",
    textAlign: "right",
    fontWeight: "500",
    color: "#1F2937",
  },
  moreItemsRow: {
    marginTop: "6px",
    paddingTop: "6px",
    borderTop: "1px dashed #E5E7EB",
  },
  moreItems: {
    fontSize: "clamp(11px, 1.2vw, 12px)",
    color: "#FF6B35",
    fontWeight: "500",
    textAlign: "center",
  },
  orderCardFooter: {
    padding: "12px 16px",
    borderTop: "1px solid #F0F0F0",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  totalContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    width: "100%",
  },
  footerItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },
  footerItemTotal: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },
  footerLabel: {
    fontSize: "clamp(12px, 1.2vw, 15px)",
    fontWeight: "500",
    color: "#9CA3AF",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  footerValue: {
    fontSize: "clamp(13px, 1.2vw, 16px)",
    fontWeight: "600",
    color: "#1F2937",
  },
  discountLabel: {
    fontSize: "clamp(11px, 1.2vw, 12px)",
    fontWeight: "500",
    color: "#DC2626",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  discountValue: {
    fontSize: "clamp(12px, 1.2vw, 14px)",
    fontWeight: "600",
    color: "#DC2626",
  },
  refundedLabel: {
    fontSize: "clamp(11px, 1.2vw, 12px)",
    fontWeight: "500",
    color: "#DC2626",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  refundedValue: {
    fontSize: "clamp(12px, 1.2vw, 14px)",
    fontWeight: "600",
    color: "#DC2626",
  },
  serviceChargeFooterLabel: {
    fontSize: "clamp(11px, 1.2vw, 12px)",
    fontWeight: "500",
    color: "#FF6B35",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  serviceChargeFooterValue: {
    fontSize: "clamp(12px, 1.2vw, 14px)",
    fontWeight: "600",
    color: "#FF6B35",
  },
  footerDivider: {
    height: "1px",
    backgroundColor: "#E5E7EB",
    margin: "4px 0",
  },
  footerTotalLabel: {
    fontSize: "clamp(13px, 1.2vw, 14px)",
    fontWeight: "700",
    color: "#1F2937",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  footerTotalValue: {
    fontSize: "clamp(16px, 1.5vw, 18px)",
    fontWeight: "700",
    color: "#000000",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  detailsButton: {
    padding: "18px 16px",
    backgroundColor: "transparent",
    border: "1px solid #E5E7EB",
    borderRadius: "10px",
    fontSize: "clamp(12px, 1.2vw, 14px)",
    fontWeight: "600",
    color: "#000000",
    cursor: "pointer",
    transition: "all 0.2s",
    minHeight: "60px",
    // flex: "1 1 auto",
    minWidth: "146px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  reprintButton: {
    padding: "18px 16px",
    backgroundColor: "#3B82F6",
    border: "none",
    borderRadius: "10px",
    fontSize: "clamp(12px, 1.2vw, 14px)",
    fontWeight: "600",
    color: "white",
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60px",
    // flex: "1 1 auto",
    minWidth: "146px",
  },
  payButton: {
    padding: "18px 16px",
    backgroundColor: "#000000",
    border: "none",
    borderRadius: "10px",
    fontSize: "clamp(12px, 1.2vw, 14px)",
    fontWeight: "600",
    color: "white",
    cursor: "pointer",
    transition: "all 0.2s",
    minHeight: "60px",
    // flex: "1 1 auto",
    minWidth: "146px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  allVoidedLabel: {
    padding: "12px 16px",
    backgroundColor: "#FEE2E2",
    color: "#DC2626",
    borderRadius: "10px",
    fontSize: "clamp(12px, 1.2vw, 13px)",
    fontWeight: "600",
    minHeight: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "1 1 auto",
    minWidth: "80px",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    width: "100%",
  },
  emptyTitle: {
    fontSize: "clamp(16px, 2vw, 18px)",
    fontWeight: "600",
    color: "#1F2937",
    marginTop: "16px",
  },
  emptySubtitle: {
    fontSize: "clamp(13px, 1.2vw, 14px)",
    color: "#9CA3AF",
    marginTop: "8px",
    wordBreak: "break-word",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
    marginTop: "24px",
    padding: "16px",
    backgroundColor: "white",
    borderRadius: "12px",
    flexWrap: "wrap",
  },
  paginationButton: {
    padding: "12px 16px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "clamp(14px, 1.2vw, 16px)",
    fontWeight: "500",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minHeight: "44px",
    minWidth: "80px",
    justifyContent: "center",
  },
  paginationInfo: {
    fontSize: "clamp(13px, 1.2vw, 15px)",
    color: "#6B7280",
    textAlign: "center",
  },
};
